import { useState } from "react";
import { Plus, Search, Filter, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useProdutos } from "@/features/produtos/hooks/useProdutos";
import { useProductGroups } from "@/features/produtos/hooks/useProductGroups";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type Ordenacao = "codigo" | "descricao";
type Situacao = "all" | "ativos" | "inativos";

const PAGE_SIZES = [10, 20, 50, 100] as const;

const TIPO_PRODUTO_LABELS: Record<number, string> = {
  1: "Mercadoria",
  2: "Matéria Prima",
  3: "Semi-Acabado",
  4: "Prod. Acabado",
  5: "Embalagem",
  6: "Uso/Consumo",
  7: "Ativo Imobiliz.",
  8: "Serviço",
  9: "Combustível",
};

const Produtos = () => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery]         = useState("");
  const [typeFilter, setTypeFilter]           = useState<string>("all");
  const [groupFilter, setGroupFilter]         = useState<string>("all");
  const [statusFilter, setStatusFilter]       = useState<Situacao>("all");
  const [ordenarPor, setOrdenarPor]           = useState<Ordenacao>("codigo");
  const [decrescente, setDecrescente]         = useState(false);
  const [pagina, setPagina]                   = useState(1);
  const [tamanhoPagina, setTamanhoPagina]     = useState<number>(20);

  const termo = useDebounce(searchQuery.trim(), 400);
  const { groups } = useProductGroups();

  // ── Filtros, ordenação e paginação server-side ────────────────────────────
  const { produtos: displayProdutos, total, isLoading, isFetching } = useProdutos({
    pagina,
    tamanhoPagina,
    filtros: {
      termo,
      tipoProduto: typeFilter === "all" ? undefined : Number(typeFilter),
      grupoId: groupFilter === "all" ? undefined : Number(groupFilter),
      situacao: statusFilter === "all" ? undefined : statusFilter,
      ordenarPor,
      decrescente,
    },
  });

  const temFiltro =
    searchQuery.trim() !== "" || typeFilter !== "all" || groupFilter !== "all" || statusFilter !== "all";

  // ── Paginação ─────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / tamanhoPagina));
  const firstItem  = (pagina - 1) * tamanhoPagina + 1;
  const lastItem   = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPagina(1);
  };

  // Clicar na coluna já ordenada inverte o sentido; em outra coluna, começa crescente.
  const handleSort = (coluna: Ordenacao) => {
    if (coluna === ordenarPor) {
      setDecrescente((d) => !d);
    } else {
      setOrdenarPor(coluna);
      setDecrescente(false);
    }
    setPagina(1);
  };

  const limparFiltros = () => {
    setSearchQuery("");
    setTypeFilter("all");
    setGroupFilter("all");
    setStatusFilter("all");
    setPagina(1);
  };

  const SortIcon = ({ coluna }: { coluna: Ordenacao }) =>
    coluna !== ordenarPor ? (
      <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />
    ) : decrescente ? (
      <ArrowDown className="h-3.5 w-3.5 ml-1" />
    ) : (
      <ArrowUp className="h-3.5 w-3.5 ml-1" />
    );

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Produtos</h1>
        <Button onClick={() => navigate("/produtos/novo")}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {/* ── Filtros ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código, descrição ou código de barras..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPagina(1); }}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Tipos</SelectItem>
            {Object.entries(TIPO_PRODUTO_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={groupFilter} onValueChange={(v) => { setGroupFilter(v); setPagina(1); }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Grupo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Grupos</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.id} value={String(g.id)}>{g.descricao || `Grupo ${g.id}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as Situacao); setPagina(1); }}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Situação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Ativos e Inativos</SelectItem>
            <SelectItem value="ativos">Somente Ativos</SelectItem>
            <SelectItem value="inativos">Somente Inativos</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={ordenarPor}
          onValueChange={(v) => { setOrdenarPor(v as Ordenacao); setDecrescente(false); setPagina(1); }}
        >
          <SelectTrigger className="w-[230px]">
            <ArrowUpDown className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="codigo">Ordenar por Código</SelectItem>
            <SelectItem value="descricao">Ordenar por Descrição</SelectItem>
          </SelectContent>
        </Select>

        {temFiltro && (
          <Button variant="ghost" size="sm" onClick={limparFiltros}>
            <X className="h-4 w-4 mr-1" />
            Limpar filtros
          </Button>
        )}
      </div>

      {/* ── Tabela ─────────────────────────────────────────────────── */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">
                <button type="button" className="inline-flex items-center hover:text-foreground" onClick={() => handleSort("codigo")}>
                  Código
                  <SortIcon coluna="codigo" />
                </button>
              </TableHead>
              <TableHead>
                <button type="button" className="inline-flex items-center hover:text-foreground" onClick={() => handleSort("descricao")}>
                  Descrição
                  <SortIcon coluna="descricao" />
                </button>
              </TableHead>
              <TableHead>Cód. Barras</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Preço</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : displayProdutos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  Nenhum produto encontrado
                </TableCell>
              </TableRow>
            ) : (
              displayProdutos.map((produto) => (
                <TableRow
                  key={produto.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/produtos/${produto.id}`)}
                >
                  <TableCell className="tabular-nums text-muted-foreground">{produto.id}</TableCell>
                  <TableCell className="font-medium">
                    <div>{produto.descricao}</div>
                    {produto.descricaoReduzida && (
                      <div className="text-xs text-muted-foreground">{produto.descricaoReduzida}</div>
                    )}
                  </TableCell>
                  <TableCell>{produto.codigoBarras || "-"}</TableCell>
                  <TableCell>{TIPO_PRODUTO_LABELS[produto.tipoProduto] ?? "-"}</TableCell>
                  <TableCell className="text-right">
                    {produto.precoPadrao != null
                      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(produto.precoPadrao)
                      : "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    {produto.inativo ? (
                      <Badge variant="secondary">Inativo</Badge>
                    ) : (
                      <Badge variant="success">Ativo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/produtos/${produto.id}`);
                      }}
                    >
                      Ver Detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Rodapé de paginação ────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {total === 0
            ? "Nenhum registro"
            : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? "s" : ""}`}
          {isFetching && !isLoading && (
            <span className="ml-2 text-xs">(atualizando...)</span>
          )}
        </p>

        <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
              <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[80px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((s) => (
                    <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina(1)}
                disabled={pagina === 1}
              >
                «
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina((p) => p - 1)}
                disabled={pagina === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (pagina <= 3) {
                  page = i + 1;
                } else if (pagina >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = pagina - 2 + i;
                }
                return (
                  <Button
                    key={page}
                    variant={pagina === page ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8 text-xs"
                    onClick={() => setPagina(page)}
                  >
                    {page}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina((p) => p + 1)}
                disabled={pagina >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPagina(totalPages)}
                disabled={pagina >= totalPages}
              >
                »
              </Button>
            </div>

            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Página {pagina} de {totalPages}
            </span>
        </div>
      </div>
    </div>
  );
};

export default Produtos;
